import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { CataloguePageHeader } from '@/components/catalogue/Header';
import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { DecimalField } from '@/components/form/fields/text/DecimalInput';
import { NumberField } from '@/components/form/fields/text/NumberInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import InfoTooltip from '@/components/tooltip/InfoTooltip';
import SiChevronLeft from '@/icons/SiChevronLeft';
import { useBundle } from '@/services/bundles/useBundleService';
import { calculateDeviceRequirementAnnual } from '@/services/catalogue/helper';
import {
  useSaveBundleWeighting,
  useSaveStockParameters,
  useSetStockParameters,
  useStockParameters
} from '@/services/catalogue/useCustomerCatalogueService';
import { CatalogueStep, useNavigateAndValidate } from '@/services/catalogue/validation/catalogueHooks';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiAlertTriangle } from 'react-icons/fi';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';
import { CustomerCatalogueContainer } from '../CustomerCatalogueContainer';
import StockUserGuideButton from './stockLevels/StockUserGuideButton';

const StockParametersFormFields = z.object({
  numberOfEmployees: z.number().min(1),
  churnPercentage: z.number().min(0),
  gsdPercentage: z.number(),
  bundleWeightings: z.array(z.object({ bundleId: z.string(), weightingPercentage: z.number().min(0) }))
});

type StockParametersFormFields = z.infer<typeof StockParametersFormFields>;

const StockParametersContent: FunctionComponent = () => {
  const routePrefix = useCustomerRoutePrefix();
  const navigateAndValidate = useNavigateAndValidate();

  const { stockParameters, bundleWeightings } = useStockParameters();
  const { setStockParameters, setBundleWeighting } = useSetStockParameters();
  const saveStockParameters = useSaveStockParameters();
  const saveBundleWeighting = useSaveBundleWeighting();
  const bundles = useBundle(state => state.bundles);

  const breadcrumbs: NavPage[] = [
    {
      name: 'Stock Calculator',
      to: `${routePrefix}/stockCalculator`
    },
    {
      name: 'Set Bundle Stock Parameters',
      to: ''
    }
  ];

  const methods = useForm<StockParametersFormFields>({
    resolver: zodResolver(StockParametersFormFields),
    defaultValues: {
      numberOfEmployees: stockParameters.numberOfEmployees || 0,
      churnPercentage: stockParameters.churnRatePercentage || 0,
      gsdPercentage: stockParameters.gsdPercentage || 0,
      bundleWeightings: bundleWeightings.map(b => ({ id: b.bundleId, weightingPercentage: b.weightingPercentage }))
    }
  });

  // HACK: The first time we load this page, the bundle weightings don't exist. They get created in a useEffect in
  // useCustomerCatalogueService. As a result, the form can get initialised with the wrong bundle weightings.
  // We need to reset the form if the bundle weightings ever change.
  useEffect(() => {
    methods.setValue('bundleWeightings', bundleWeightings);
  }, [methods, bundleWeightings]);

  const [navigateNeeded, setNavigateNeeded] = useState<boolean>(false);

  useEffect(() => {
    // ANNOYING HACK
    // The navigation service is set up to use data from the Customer Catalogue service.
    // On the other pages, changes are automatically stored in the service, so it's all fine.
    // Here, we only store the changes when we submit a form.
    // If we try to submit the changes and then navigate, the navigation uses the old data, which isn't correct.
    // Instead, we need to have the submit function store the changes, then trigger this useEffect, so it's calling
    // a version of the navigate function which has the correct data.
    if (navigateNeeded) {
      navigateAndValidate(`${routePrefix}/advanced/bundleStockLevels`, CatalogueStep.SELECT_STOCK_PARAMETERS);
    }

    setNavigateNeeded(false);
  }, [navigateNeeded, setNavigateNeeded, navigateAndValidate]);

  const [showBundleWeightingInfoMessage, setShowBundleWeightingInfoMessage] = useState(false);

  useEffect(() => {
    const total = bundleWeightings.reduce((sum, item) => sum + item.weightingPercentage, 0);

    if (total !== 100) {
      setShowBundleWeightingInfoMessage(true);
    } else {
      setShowBundleWeightingInfoMessage(false);
    }
  }, [bundleWeightings]);

  const debouncedSaveStockParameters = useDebouncedCallback(async (fieldKey: string, value: number) => {
    const payload = {
      ...stockParameters
    };

    // @ts-ignore
    payload[fieldKey] = value;

    setStockParameters(payload);
    await saveStockParameters();
  }, 800);

  const debouncedSaveBundleWeighting = useDebouncedCallback(async (bundleId: string, value: number) => {
    setBundleWeighting(bundleId, Number(value));
    await saveBundleWeighting(bundleId);
  }, 800);

  return (
    <div className="flex h-full flex-1 flex-col bg-white bg-opacity-50">
      <CataloguePageHeader title="Stock Calculator" subtitle={'Set Bundle Stock Parameters'} breadcrumbs={breadcrumbs}>
        <div className="flex gap-4">
          {/*<HelpButton />*/}
          <Button
            onClick={() => {
              navigateAndValidate(`${routePrefix}/bundles`);
            }}
            variant={ButtonVariant.Mono}
          >
            <SiChevronLeft className="text-xl" />
            <span>Back to Bundles</span>
          </Button>
          <StockUserGuideButton />
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => {
              setNavigateNeeded(true);
            }}
          >
            Review / Manage Bundle Stock Levels
          </Button>
        </div>
      </CataloguePageHeader>
      <div className="overflow-y-auto px-6 py-8 space-y-5 flex-1">
        <Form className="flex w-full flex-col space-y-10" methods={methods} onSubmit={() => {}}>
          <div>
            <span className="text-lg mb-5">Stock Level Parameters</span>
            <div className="grid grid-cols-2 gap-5 max-w-4xl">
              <NumberField
                onChangeCallback={async (newValue: number) => {
                  await debouncedSaveStockParameters('numberOfEmployees', newValue);
                }}
                id={'numberOfEmployees'}
                label="Number of Employees"
                min={1}
              />
              <NumberField
                onChangeCallback={async (newValue: number) => {
                  await debouncedSaveStockParameters('churnRatePercentage', Number(newValue));
                }}
                id={'churnPercentage'}
                label="Annual Employee Turnover Rate (%)"
                min={0}
                tooltipText="Annual Employee Turnover Rate is the percentage of employees that leave and join the business in a year on a ‘business as usual’ basis i.e. not because of growth or decline. This allows us to anticipate the number of device collections and/or replacements required to deliver the service. For example, if a business had 100 employees and an average of 5 leave and 5 join to replace them per year, the turnover rate is 5%, so enter the number 5."
              />
              <TextField
                onlyDigits
                onChangeCallback={async (newValue: number) => {
                  await debouncedSaveStockParameters('gsdPercentage', Number(newValue));
                }}
                id={'gsdPercentage'}
                label="Annual Employee Growth Rate (%)"
                tooltipText="Annual Employee Growth Rate describes the rate at which the need for employees is growing, as a proxy for business growth, to forecast the number of devices needed. This allows us to advise stock holding requirements to fuel that growth without an interruption to service. For example, if the business employee requirement is growing at 5% per year, enter 5. If the business is static year on year, then enter the value 0. If the business is forecast to decline, a negative number can be entered such as -5."
              />
              <NumberField
                readonly
                id={'deviceRequirementAnnual'}
                label="Suggested Annual Device Requirement"
                tooltipText="This field is auto calculated from the answers provided to forecast the number of devices needed to project bin level or stocking level needed to provide a seamless service. Below and on the next page allows further information to be provided to put this into practice"
                overrideValue={calculateDeviceRequirementAnnual(
                  methods.getValues().numberOfEmployees,
                  Number(methods.getValues().churnPercentage),
                  Number(methods.getValues().gsdPercentage)
                )}
              />
            </div>
          </div>

          {bundles.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <span className="text-lg">Bundle Weightings</span>
                <InfoTooltip
                  id="bundleWeighting"
                  text="Please enter here the priority weighting you would like to assign to each job role bundle. For example, if you have two job role bundles such as ‘Sales’ and ‘Marketing’ and they have the same number of people in the department, you may wish to enter ‘50’ in each role field. This will create a 50/50 split. "
                />
              </div>
              <div className="grid gap-5 max-w-6xl grid-cols-3">
                {bundles.map(bundle => {
                  const weightingIndex = bundleWeightings.findIndex(b => b.bundleId === bundle.id);

                  if (weightingIndex === -1) {
                    return <></>;
                  }

                  return (
                    <DecimalField
                      maxDp={1}
                      key={bundle.id}
                      id={`bundleWeightings.${weightingIndex}.weightingPercentage`}
                      label={bundle.name}
                      onChangeCallback={async (newValue: number) => {
                        await debouncedSaveBundleWeighting(bundle.id, newValue);
                      }}
                      max={100}
                    />
                  );
                })}
              </div>
              {showBundleWeightingInfoMessage && (
                <div className="font-semibold mt-3 flex space-x-2 text-red-500 items-center">
                  <FiAlertTriangle className="text-xl" />
                  <span>Bundle weightings must add up to 100</span>
                </div>
              )}
            </div>
          )}
        </Form>
      </div>
    </div>
  );
};

export const StockParameters: FunctionComponent = () => {
  return (
    <CustomerCatalogueContainer>
      <StockParametersContent />
    </CustomerCatalogueContainer>
  );
};
